/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import {
  addVehicleBindQuery,
  addVehicleInfoQuery,
  fromAddVehicleInfoActions,
  fromAddVehicleQuoteActions
} from '@amfam/policy/add-vehicle/data-access';
import { PolicySelectors as policyQuery, PolicySelectors } from '@amfam/policy/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { OverviewAnalytics } from '@amfam/shared/models';
import { agentQuery } from '@amfam/shared/utility/agent';
import { Facade as BrandFacade, BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

import { QuickQuoteFeatureFacade } from '../../../../../replace-vehicle/feature/src/lib/+state/quick-quote-feature/quick-quote-feature.facade'; // TODO temp fix for circular dependency
import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import * as addVehicleFeatureSelector from '../+state/add-vehicle-feature.selectors';
import { AddVehicleFeatureService } from '../services/add-vehicle-feature.service';

@Component({
  selector: 'ds-vehicle-offline-bind',
  templateUrl: './vehicle-offline-bind.component.html',
  styleUrls: ['../add-vehicle-container/add-vehicle-container.component.scss'],
  standalone: false
})
export class VehicleOfflineBindComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();
  @Output() errorEvent = new EventEmitter<unknown>();
  @Output() notifyAgentEvent = new EventEmitter<{ isProcessExited: boolean }>();

  public vehicleRiskDescription$ = this.store.select(vehicleQuery.selectNewRiskDescription);
  public agents$ = this.store.select(agentQuery.getRealAgents);
  public sendToBtnText: string;
  public headerContentPath: string;
  public bodyContentPath: string;
  public pageName: string;
  public vehicleOfflineBind = this.formBuilder.group({});
  public classicOnlyAutoPolicyOrCompOnlyAdv = false;
  public existingVehicleId: string;
  public exitFlowModalId = this.modalService.createModalId('exitFlowModalId');

  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private formBuilder: UntypedFormBuilder,
    private store: Store,
    private copyService: CopyService,
    private spinnerService: LoadingSpinnerService,
    private addVehicleFeatureService: AddVehicleFeatureService,
    public brandFacade: BrandFacade,
    private analyticsFacade: AnalyticsFacade,
    private featureFlagService: FeatureFlagService,
    private route: ActivatedRoute,
    private dockingBarService: DockingBarService,
    private modalService: DsModalService
  ) {
    this.brandFacade.isPartner$.subscribe(value => {
      this.sendToBtnText = value
        ? this.copyService.getCopy('policy.sendToLicensedAdvisor')
        : this.copyService.getCopy('policy.newVehicleSendToAgent');
      this.headerContentPath = value
        ? 'policy.newVehicleAdvisorsHelpHeader'
        : 'policy.newVehicleAgentsHelpHeader';
      this.bodyContentPath = value
        ? 'policy.newVehicleAdvisorsHelpBody'
        : 'policy.newVehicleAgentsHelpBody';
      this.pageName = value ? 'NeedAdvisor' : 'NeedAgent';
    });

    combineLatest([
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(PolicySelectors.selectHasClassicAutoPolicies),
      this.store.select(PolicySelectors.selectHasAdvanceAutoPolicies),
      this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage),
      this.store.select(vehicleQuery.selectNewVehicle)
    ])
      .pipe(
        map(
          ([
            isPartner,
            hasClassicAutoPolicy,
            hasAdvanceAutoPolicy,
            hasOnlyPolicyComprehensiveCoverage,
            newVehicle
          ]) => ({
            isPartner,
            hasClassicAutoPolicy,
            hasAdvanceAutoPolicy,
            hasOnlyPolicyComprehensiveCoverage,
            newVehicle
          })
        ),
        take(1)
      )
      .subscribe(
        ({
          isPartner,
          hasClassicAutoPolicy,
          hasAdvanceAutoPolicy,
          hasOnlyPolicyComprehensiveCoverage,
          newVehicle
        }) => {
          const advanceCompOnly =
            hasAdvanceAutoPolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage;

          const classicOnlyAutoPolicy = hasClassicAutoPolicy && !hasAdvanceAutoPolicy && !isPartner;

          if (advanceCompOnly || classicOnlyAutoPolicy) {
            this.classicOnlyAutoPolicyOrCompOnlyAdv = true;
            this.headerContentPath = 'policy.newVehicleAgentsNeedsReviewHelpHeader';
            this.bodyContentPath = 'policy.newVehicleAgentsNeedsReviewHelpBody';

            this.pageName = 'NeedAgent';
            if (classicOnlyAutoPolicy) {
              this.store.dispatch(
                VehicleInfoActions.addVehicleHardStopSendAnalytics({
                  payload: { pageAnalyticName: 'ClassicPolicy' }
                })
              );
              this.analyticsFacade.trackEvent({
                event: 'hardstop',
                form_name: 'Add Or Replace Vehicle Flow',
                vehicle_make: _get(newVehicle, 'make', ''),
                vehicle_model: _get(newVehicle, 'model', ''),
                vehicle_year: _get(newVehicle, 'year', ''),
                product_line: 'auto',
                hard_stop_error_description: 'ClassicPolicy'
              });
            }
            if (!classicOnlyAutoPolicy && hasOnlyPolicyComprehensiveCoverage) {
              this.store.dispatch(
                VehicleInfoActions.addVehicleHardStopSendAnalytics({
                  payload: { pageAnalyticName: 'CompOnly' }
                })
              );
              this.analyticsFacade.trackEvent({
                event: 'hardstop',
                form_name: 'Add Or Replace Vehicle Flow',
                vehicle_make: _get(newVehicle, 'make', ''),
                vehicle_model: _get(newVehicle, 'model', ''),
                vehicle_year: _get(newVehicle, 'year', ''),
                product_line: 'auto',
                hard_stop_error_description: 'CompOnly'
              });
            }
          }
        }
      );
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.stop$)).subscribe(params => {
      if (params && params['id'] > 0) {
        this.existingVehicleId = params['id'];
        this.dockingBarService.registerHeading('Replace Vehicle');
      }
    });
    this.spinnerService.stop();
    this.handleError();
  }

  stayFromExitModal() {
    this.dockingBarService.registerHeading(this.copyService.getCopy('policy.replaceVehicle'));
  }

  navigateToOverview() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['overview']
      })
    );
  }

  ngAfterViewInit() {
    if (this.classicOnlyAutoPolicyOrCompOnlyAdv) {
      return;
    }
    this.store
      .select(addVehicleBindQuery.selectVehicleBindError)
      .pipe(
        take(1),
        tap((bindErr: boolean) => {
          if (bindErr) {
            this.store.dispatch(
              VehicleInfoActions.addVehicleSendAnalytics({
                payload: { pageAnalyticName: 'Error:NeedAgent' }
              })
            );
            setTimeout(() => {
              this.analyticsFacade.trackPage(OverviewAnalytics.AddVehicleErrorAnalytics);
            });
          } else {
            this.store.dispatch(
              VehicleInfoActions.addVehicleSendAnalytics({
                payload: { pageAnalyticName: this.pageName }
              })
            );
          }
        })
      )
      .subscribe();
  }

  public sendToAgent() {
    this.spinnerService.start({
      blockActions: true
    });

    this.analyticsFacade.trackEvent({
      event: 'agent_referral',
      form_name: 'Add Vehicle'
    });
    if (this.existingVehicleId) {
      this.quickQuoteFeatureFacade.sendToAgentOnCancel();
    } else {
      this.store
        .select(addVehicleFeatureSelector.selectCurrentVehicleInfo)
        .pipe(
          withLatestFrom(
            this.store.select(policyQuery.selectAdvanceAutoPolicies),
            this.store.select(addVehicleInfoQuery.selectNewVehicleDrivers),
            this.store.select(addVehicleInfoQuery.selectNewVehicleList)
          ),
          take(1)
        )
        .subscribe(([vehicleDetailsState, advancedAutoPolicies, driversList, vehicleList]) => {
          this.notifyAgentEvent.emit({
            isProcessExited:
              !(
                !_get(vehicleDetailsState, 'primaryUsageInfo.primaryUse') &&
                !!_get(vehicleDetailsState, 'isNewVehicleDiscountActive')
              ) &&
              !(
                this.addVehicleFeatureService.underWritingHardStopCheck(
                  vehicleDetailsState?.underwritingQuestions
                ) ||
                this.addVehicleFeatureService.primaryUseHardStopCheck(
                  _get(vehicleDetailsState, 'primaryUsageInfo')
                ) ||
                this.addVehicleFeatureService.garageAddressWithSingleAdvancedPolicy(
                  vehicleDetailsState,
                  advancedAutoPolicies
                ) ||
                _get(driversList, 'length', 0) > 2 * _get(vehicleList, 'length', 0)
              ) &&
              !this.addVehicleFeatureService.garageAddressWithMultipleAdvancedPolicies(
                vehicleDetailsState,
                advancedAutoPolicies
              )
          });
        });
    }
  }

  public goBackOneStep() {
    if (this.classicOnlyAutoPolicyOrCompOnlyAdv) {
      this.analyticsFacade.trackButtonClick({
        link: `MyAccount:HardStop:Classic:Back`
      });
    }

    this.resetErrors();
    this.store.dispatch(fromRouterActions.Back());
  }

  public onCancel(): void {
    if (this.existingVehicleId) {
      this.modalService.open(this.exitFlowModalId);
    } else {
      if (this.classicOnlyAutoPolicyOrCompOnlyAdv) {
        this.analyticsFacade.trackButtonClick({
          link: `MyAccount:HardStop:Classic:Cancel`
        });
      }
      this.cancelEvent.emit(true);
    }
  }

  public ngOnDestroy() {
    this.spinnerService.stop();
    this.stop$.next();
    this.stop$.complete();
  }

  private handleError() {
    this.store
      .select(addVehicleBindQuery.selectVehicleANSNotificationError)
      .pipe(takeUntil(this.stop$))
      .subscribe(vehicleFeaturesError => {
        if (vehicleFeaturesError) {
          this.store
            .select(vehicleQuery.selectNewVehicle)
            .pipe(take(1))
            .subscribe(vehicle => {
              this.analyticsFacade.trackEvent({
                event: 'hardstop',
                form_name: 'Add Or Replace Vehicle Flow',
                vehicle_make: _get(vehicle, 'make', ''),
                vehicle_model: _get(vehicle, 'model', ''),
                vehicle_year: _get(vehicle, 'year', ''),
                product_line: 'auto',
                hard_stop_error_description: 'QuoteError'
              });
            });
          this.errorEvent.emit();
          this.spinnerService.stop();
        }
      });
  }

  private resetErrors() {
    this.store.dispatch(fromAddVehicleInfoActions.addVehicleResetError());
    this.store.dispatch(fromAddVehicleQuoteActions.addVehicleRateErrorReset());
  }
}
